import CurrencyRupeeRoundedIcon from '@mui/icons-material/CurrencyRupeeRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { Box, Chip, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { FC, useMemo } from 'react';
import { formatIndianAuto } from './echartsConfig';
import { reportKpiCardShell, ReportKpiHeader } from './ReportKpiCard';
import ReportStatusStripe from './ReportStatusStripe';
import { type ConsolidatedComparisonMode, type OrgRevenueRow } from './ConsolidatedRevenueByOrgTable';
import type { BookingStatusItem } from '../../../utils/bookingStatusUtils';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';

export interface BookingStatusCountApi {
  name: string;
  value: number;
}

export interface PortfolioActualizedKpiPayload {
  portfolioActualizedRevenue?: number | null;
  portfolioActualizedRevenuePreviousPeriod?: number | null;
  portfolioActualizedCumulativeTrend?: { date: string; value: number }[];
}

function formatRupeesIndian(value: number): string {
  return `${getCurrencySymbol()}${formatIndianAuto(value)}`;
}

function formatPct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const v = Number(value);
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
}

function buildSummary(rows: OrgRevenueRow[]) {
  const empty = {
    topPerformerName: '—',
    topPerformerMtd: 0,
    fastestName: '—',
    fastestPct: null as number | null,
    avgBookingValue: 0,
    totalBookings: 0,
  };

  const n = rows.length;
  if (n === 0) {
    return empty;
  }

  let top = rows[0];
  let fastest = rows[0];
  let maxRev = -1;
  let maxPct = -Infinity;

  let totalBookings = 0;
  let totalMtdRevenue = 0;

  for (const r of rows) {
    const mtd = r.revenue?.mtd;
    const v = Number(mtd?.value) || 0;
    const bc = Number(mtd?.bookingCount) || 0;
    totalBookings += bc;
    totalMtdRevenue += v;

    if (v > maxRev) {
      maxRev = v;
      top = r;
    }
    const g = mtd?.percentageChange;
    if (g != null && !Number.isNaN(Number(g)) && Number(g) > maxPct) {
      maxPct = Number(g);
      fastest = r;
    }
  }

  const fastestPct = maxPct === -Infinity ? null : Number(fastest.revenue?.mtd?.percentageChange) || maxPct;

  return {
    topPerformerName: top.organizationName || '—',
    topPerformerMtd: Number(top.revenue?.mtd?.value) || 0,
    fastestName: fastest.organizationName || '—',
    fastestPct,
    avgBookingValue: totalBookings > 0 ? totalMtdRevenue / totalBookings : 0,
    totalBookings,
  };
}

interface ConsolidatedRevenueSummaryBannerProps {
  rows: OrgRevenueRow[];
  bookingStatusCounts: BookingStatusCountApi[];
  loading?: boolean;
  comparisonMode?: ConsolidatedComparisonMode;
  actualizedPortfolio?: PortfolioActualizedKpiPayload | null;
}

const ConsolidatedRevenueSummaryBanner: FC<ConsolidatedRevenueSummaryBannerProps> = ({
  rows,
  bookingStatusCounts,
  loading = false,
  comparisonMode = 'PRIOR_PERIOD',
  actualizedPortfolio,
}) => {
  const theme = useTheme();
  const shell = reportKpiCardShell(theme);

  const summary = useMemo(() => buildSummary(rows), [rows]);

  const growthFootnote = comparisonMode === 'YOY' ? 'vs same dates last year' : 'vs previous equivalent period';

  const hasBackendActualizedKpi =
    actualizedPortfolio != null &&
    (actualizedPortfolio.portfolioActualizedRevenue != null ||
      actualizedPortfolio.portfolioActualizedRevenuePreviousPeriod != null ||
      (actualizedPortfolio.portfolioActualizedCumulativeTrend != null &&
        actualizedPortfolio.portfolioActualizedCumulativeTrend.length > 0));

  const actCur = Number(actualizedPortfolio?.portfolioActualizedRevenue) || 0;

  const kpiGridSx = {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, 1fr)',
      md: 'repeat(3, minmax(0, 1fr))',
      lg: 'repeat(5, minmax(0, 1fr))',
    },
    gap: 1.5,
    mb: 1.25,
  } as const;

  if (loading) {
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={kpiGridSx}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={100} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const stripeData: BookingStatusItem[] = (bookingStatusCounts || []).map(b => ({
    name: b.name,
    value: Number(b.value) || 0,
  }));

  const fastestPct = summary.fastestPct;
  const fastestBadge =
    fastestPct != null && Number.isFinite(fastestPct) ? (
      <Chip
        size="small"
        label={formatPct(fastestPct)}
        sx={t => ({
          alignSelf: 'flex-start',
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          bgcolor: fastestPct >= 0 ? alpha(t.palette.success.main, 0.12) : alpha(t.palette.error.main, 0.12),
          color: fastestPct >= 0 ? t.palette.success.dark : t.palette.error.dark,
          border: '1px solid',
          borderColor: fastestPct >= 0 ? t.palette.success.light : t.palette.error.light,
        })}
      />
    ) : (
      <Chip size="small" label="—" variant="outlined" sx={{ alignSelf: 'flex-start', fontWeight: 700 }} />
    );

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={kpiGridSx}>
        {/* Top performer: org + revenue */}
        <Box sx={shell}>
          <ReportKpiHeader title="Top Performer" icon={<EmojiEventsRoundedIcon sx={{ fontSize: 16 }} />} />
          <Tooltip title={summary.topPerformerName.length > 36 ? summary.topPerformerName : ''}>
            <Typography fontWeight={700} color="text.primary" noWrap sx={{ fontSize: '0.95rem' }}>
              {summary.topPerformerName}
            </Typography>
          </Tooltip>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
              Revenue
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontSize: '1rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
              {formatRupeesIndian(summary.topPerformerMtd)}
            </Typography>
          </Box>
        </Box>

        {/* Fastest growing: org + trend badge */}
        <Box sx={shell}>
          <ReportKpiHeader title="Fastest Growing" icon={<TrendingUpRoundedIcon sx={{ fontSize: 16 }} />} />
          <Tooltip title={summary.fastestName.length > 36 ? summary.fastestName : ''}>
            <Typography fontWeight={800} color="text.primary" noWrap sx={{ fontSize: '0.95rem' }}>
              {summary.fastestName}
            </Typography>
          </Tooltip>
          {fastestBadge}
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, lineHeight: 1.35 }}>
            {growthFootnote}
          </Typography>
        </Box>

        {/* Avg booking value: amount only */}
        <Box sx={shell}>
          <ReportKpiHeader title="Avg Booking Value" icon={<CurrencyRupeeRoundedIcon sx={{ fontSize: 16 }} />} />
          <Typography
            variant="h5"
            sx={{ fontSize: '1rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
            {summary.totalBookings > 0 ? formatRupeesIndian(summary.avgBookingValue) : '—'}
          </Typography>
        </Box>

        {/* Total bookings: count */}
        <Box sx={shell}>
          <ReportKpiHeader title="Total Bookings" icon={<EventNoteRoundedIcon sx={{ fontSize: 16 }} />} />
          <Typography
            variant="h5"
            sx={{ fontSize: '1rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
            {summary.totalBookings.toLocaleString('en-IN')}
          </Typography>
        </Box>

        {/* Total revenue (portfolio actualized) */}
        <Box sx={shell}>
          <ReportKpiHeader title="Total revenue" icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} />} />
          <Typography
            variant="h5"
            sx={{ fontSize: '1rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
            {hasBackendActualizedKpi ? formatRupeesIndian(actCur) : '—'}
          </Typography>
        </Box>
      </Box>

      {stripeData.length > 0 ? (
        <Box sx={{ mt: 0.5 }}>
          <ReportStatusStripe data={stripeData} />
        </Box>
      ) : (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: theme.palette.common.white,
            borderRadius: 2,
            px: 1.5,
            py: 1.25,
          }}>
          <Typography variant="caption" color="text.secondary">
            No booking status breakdown for the selected range.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ConsolidatedRevenueSummaryBanner;
